import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Circle, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Search, MapPin, Check, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { COMMUNES, normalize, firstLetter, slugify } from "@/data/communes";

const LETTERS = ["A", "B", "C", "D", "E", "F", "G", "I", "L", "M", "N", "O", "P", "R", "S", "T", "U", "V"];
const NICE = [43.7102, 7.262];

export default function ZoneIntervention() {
  const [query, setQuery] = useState("");
  const [letter, setLetter] = useState(null);

  const filtered = useMemo(() => {
    const q = normalize(query.trim());
    return COMMUNES.filter((c) => {
      const okQuery = !q || normalize(c).includes(q);
      const okLetter = !letter || firstLetter(c) === letter;
      return okQuery && okLetter;
    });
  }, [query, letter]);

  return (
    <div data-testid="zone-page">
      <Seo title="Zone d'intervention — Alpes-Maritimes (06) | Sandro Services 06" description="Sandro Services 06 intervient dans les 163 communes des Alpes-Maritimes (06). Recherchez votre commune : Nice, Cannes, Grasse, Antibes, Menton et bien d'autres." />
      <PageHero chapter="Zone d'intervention" title="Nos zones d'intervention" subtitle="Sandro Services 06 intervient dans les Alpes-Maritimes (06), soit les 163 communes du département." />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-14 md:py-20 grid lg:grid-cols-2 gap-12 items-start">
        {/* MAP */}
        <Reveal className="relative">
          <div className="h-[420px] md:h-[560px] rounded-2xl overflow-hidden border border-line shadow-soft">
            <MapContainer center={[43.9, 7.15]} zoom={9} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }} data-testid="zone-map">
              <TileLayer
                attribution='&copy; OpenStreetMap'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Circle center={[43.95, 7.15]} radius={42000} pathOptions={{ color: "#738259", fillColor: "#738259", fillOpacity: 0.1, weight: 1.5 }} />
              <CircleMarker center={NICE} radius={9} pathOptions={{ color: "#1E3F2D", fillColor: "#738259", fillOpacity: 1, weight: 3 }}>
                <Popup>Alpes-Maritimes (06)<br />Zone d'intervention</Popup>
              </CircleMarker>
            </MapContainer>
          </div>
          <p className="mt-4 text-sm text-ink-soft flex items-center gap-2">
            <MapPin size={16} className="text-olive" /> Intervention dans l'ensemble du département des Alpes-Maritimes.
          </p>
        </Reveal>

        {/* SEARCH + LIST */}
        <Reveal delay={0.1}>
          <h2 className="font-display text-3xl md:text-4xl text-forest">Rechercher une commune</h2>

          <div className="mt-6 relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
            <input
              data-testid="commune-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ex : Roquestéron, Nice, Cannes…"
              className="w-full rounded-full border border-line bg-white pl-12 pr-5 py-4 text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-2 focus:ring-olive focus:ring-offset-2 focus:ring-offset-sand transition-all"
            />
          </div>

          <div className="mt-5 flex flex-wrap gap-2" data-testid="letter-filter">
            <button onClick={() => setLetter(null)} className={`w-9 h-9 rounded-full text-sm font-semibold transition-colors ${!letter ? "bg-forest text-sand" : "bg-white border border-line text-ink-soft hover:border-olive"}`}>Tous</button>
            {LETTERS.map((l) => (
              <button key={l} data-testid={`letter-${l}`} onClick={() => setLetter(l === letter ? null : l)} className={`w-9 h-9 rounded-full text-sm font-semibold transition-colors ${letter === l ? "bg-olive text-white" : "bg-white border border-line text-ink-soft hover:border-olive"}`}>{l}</button>
            ))}
          </div>

          <p className="mt-6 text-sm text-ink-soft" data-testid="commune-count">
            {filtered.length} commune{filtered.length > 1 ? "s" : ""} — Alpes-Maritimes (06)
          </p>

          <div className="mt-3 max-h-[360px] overflow-y-auto pr-2 grid sm:grid-cols-2 gap-2" data-testid="commune-list">
            {filtered.map((c) => (
              <Link key={c} to={`/zone/${slugify(c)}`} data-testid="commune-item" className="group flex items-center gap-2 bg-white border border-line rounded-xl px-4 py-3 text-sm hover:border-olive hover:shadow-soft transition-all">
                <Check size={14} className="text-olive shrink-0" />
                <span className="text-ink flex-1">{c}</span>
                <ArrowRight size={13} className="text-ink-soft/40 group-hover:text-olive group-hover:translate-x-0.5 transition-all" />
              </Link>
            ))}
            {filtered.length === 0 && (
              <p className="text-ink-soft text-sm col-span-2 py-8 text-center">Aucune commune trouvée pour « {query} ».</p>
            )}
          </div>

          {filtered.length === 1 && (
            <Link to={`/zone/${slugify(filtered[0])}`} className="mt-6 flex items-center justify-between gap-4 bg-sand-100 border border-line rounded-2xl px-5 py-4 text-sm text-forest hover:border-olive transition-colors">
              <span><strong>{filtered[0]}</strong> — Alpes-Maritimes (06). Sandro Services 06 intervient dans cette commune.</span>
              <ArrowRight size={16} className="text-olive shrink-0" />
            </Link>
          )}

          <Link to="/contact" data-testid="zone-devis-btn" className="mt-8 inline-flex items-center gap-2 text-forest font-semibold border-b-2 border-olive pb-1 hover:gap-3 transition-all">
            Demander un devis pour ma commune <ArrowRight size={16} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
